import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, Variants } from "framer-motion";
import {
    X,
    Home,
    Briefcase,
    Grid3x3,
    ShoppingBag,
    Phone,
    Info,
    HelpCircle,
    FileText,
    Shield,
    Mail,
    Calendar,
} from "lucide-react";

interface MenuItem {
    path: string;
    label: string;
    icon: React.ElementType;
    description?: string;
}

interface MenuSection {
    title: string;
    items: MenuItem[];
}

interface BottomSheetMenuProps {
    isOpen: boolean;
    onClose: () => void;
}

const BottomSheetMenu: React.FC<BottomSheetMenuProps> = ({ isOpen, onClose }) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [isOpen]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isOpen) onClose();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    const menuSections: MenuSection[] = [
        {
            title: "",
            items: [
                { path: "/", label: "Accueil", icon: Home, description: "Page d'accueil" },
                { path: "/services", label: "Services", icon: Briefcase, description: "Nos prestations" },
                { path: "/projects", label: "Projets", icon: Grid3x3, description: "Nos réalisations" },
                { path: "/boutique", label: "Boutique", icon: ShoppingBag, description: "Acheter nos produits" },
                { path: "/contact", label: "Contact", icon: Phone, description: "Nous écrire" },
                { path: "/devis", label: "Demander un devis", icon: FileText, description: "Obtenez un devis personnalisé" },
            ],
        }
        // {
        //     title: "À propos",
        //     items: [
        //         { path: "/a-propos", label: "À propos", icon: Info, description: "Notre histoire" },
        //         { path: "/faq", label: "FAQ", icon: HelpCircle, description: "Questions fréquentes" },
        //         { path: "/blog", label: "Blog", icon: Mail, description: "Actualités" },
        //         { path: "/rendez-vous", label: "Rendez-vous", icon: Calendar, description: "Prendre un RDV" },
        //     ],
        // },
        // {
        //     title: "Légal",
        //     items: [
        //         { path: "/legal/mentions", label: "Mentions légales", icon: FileText, description: "Informations légales" },
        //         { path: "/legal/privacy", label: "Confidentialité", icon: Shield, description: "Politique de confidentialité" },
        //         { path: "/legal/cgu", label: "CGU", icon: FileText, description: "Conditions générales" },
        //     ],
        // },
    ];

    const overlayVariants: Variants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.3 } },
        exit: { opacity: 0, transition: { duration: 0.2 } },
    };

    const sheetVariants: Variants = {
        hidden: { y: "100%" },
        visible: {
            y: 0,
            transition: { type: "spring", damping: 25, stiffness: 300 },
        },
        exit: { y: "100%", transition: { duration: 0.25 } },
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm md:hidden"
                        variants={overlayVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        onClick={onClose}
                    />

                    <motion.div
                        className="fixed bottom-0 left-0 right-0 z-50 rounded-t-2xl bg-black shadow-xl md:hidden border-t border-gray-800"
                        variants={sheetVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                    >


                        <div className="flex justify-between items-center px-4 pt-2 pb-1">
                            <h3 className="text-lg font-bold text-white">
                                Menu
                            </h3>
                            <button
                                onClick={onClose}
                                className="p-1.5 rounded-full bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
                                aria-label="Fermer"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="px-4 pb-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
                            {menuSections.map((section, idx) => (
                                <div key={idx} className="mb-5 last:mb-0">
                                    <h4 className="text-xs font-bold text-primary uppercase tracking-wider mb-2 px-1">
                                        {section.title}
                                    </h4>
                                    <div className="grid grid-cols-2 gap-2">
                                        {section.items.map((item) => (
                                            <Link
                                                key={item.path}
                                                to={item.path}
                                                onClick={onClose}
                                                className="group flex flex-col items-center gap-1.5 p-2 rounded-xl bg-gray-900 hover:bg-gray-800 transition-all duration-200 border border-gray-800 hover:border-gray-600"
                                            >
                                                <div className="p-1.5 rounded-full bg-gray-800 text-white group-hover:text-primary group-hover:bg-gray-700 group-hover:scale-105 transition-all duration-200">
                                                    <item.icon size={16} strokeWidth={1.8} />
                                                </div>
                                                <div className="text-center">
                                                    <div className="text-sm font-medium text-white group-hover:text-primary transition-colors">
                                                        {item.label}
                                                    </div>
                                                    {item.description && (
                                                        <div className="text-[11px] text-gray-300 group-hover:text-white transition-colors">
                                                            {item.description}
                                                        </div>
                                                    )}
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="pb-4" />
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default BottomSheetMenu;